/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import dayjs from "dayjs";
import humanizeDuration from "humanize-duration";

import { OmitByKey } from "@/types/customTypes";

import { checkNever } from "./checkNever";
import { doIt } from "./doIt";
import round from "./round";

//

class Formatter {
  /** @example date() => "13.04.2003" // DD.MM.YYYY */

  public date(date?: dayjs.ConfigType) {
    return dayjs(date).format("DD.MM.YYYY");
  }

  //

  /** @example dateTime() => "13.04.2003 23:59" // DD.MM.YYYY HH:mm */

  public dateTime(date?: dayjs.ConfigType) {
    return dayjs(date).format("DD.MM.YYYY HH:mm");
  }

  //

  /** @example preciseTime() => "23:59:59" // HH:mm:ss */

  public preciseTime(date?: dayjs.ConfigType) {
    return dayjs(date).format("HH:mm:ss");
  }

  //

  /** @example time() => "23:59" // HH:mm */

  public time(date?: dayjs.ConfigType) {
    return dayjs(date).format("HH:mm");
  }

  //

  /**
   * @example
   * percentage(0.123456) // 12.35%
   * percentage(0.123456, 0) // 12%
   * percentage(0.123456, 1) // 12.3%
   * percentage(0.123456, 3) // 12.346%
   * percentage(Infinity) // ∞
   */

  public percentage = (percentage: number, signs = 2) => {
    if (percentage === Infinity) return "∞";
    return round.afterSign(percentage * 100, signs) + "%";
  };

  //

  /**
   * @param [symbol] `₸` по умолчанию
   * @example
   * currency(1000) // 1 000₸
   * currency(1000, '$') // 1 000$
   * currency(Infinity) // ∞
   */

  public currency(value: number, symbol = "₸") {
    if (value === Infinity) return "∞";
    const number = round.afterSign(value, 0);
    const localeNumber = Number(number).toLocaleString("ru");
    return `${localeNumber}${symbol}`;
  }

  //

  /**
   * @example
   * number(1000) // 1 000
   * number(1000, ' $') // 1 000 $
   * number(Infinity) // ∞
   */

  public number(value: number, options: { symbol?: string } = {}) {
    if (value === Infinity) return "∞";
    const { symbol = "" } = options;
    const number = round.afterSign(value, 0);
    const localeNumber = Number(number).toLocaleString("ru");
    return `${localeNumber}${symbol}`;
  }

  //

  /**
   * Форматирует номер телефона в формат +7 (999) 999-99-99
   *
   * @example
   * beautyPhone('7-999-999-99-99') // +7 (999) 999-99-99
   * beautyPhone('79999999999') // +7 (999) 999-99-99
   * beautyPhone('999 999 9999') // +7 (999) 999-99-99
   * beautyPhone('8 (999) 999-99-99') // +7 (999) 999-99-99
   */

  public beautyPhone(phoneNumber: string | number): string {
    const cleanedPhone = this.cleanPhone(phoneNumber);
    if (cleanedPhone.length !== 11) throw new Error("Некорректный номер");
    return cleanedPhone.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, "+$1 ($2) $3-$4-$5");
  }

  //

  /**
   * @example
   * // Пример 1: Локальный номер с добавлением кода страны
   * cleanPhone("8 (700) 123-45-67") // Результат: "77001234567"
   *
   * // Пример 2: Номер с кодом страны, без изменения
   * cleanPhone("+7 700 123 45 67", true) // Результат: "77001234567"
   *
   * // Пример 3: Удаление кода страны
   * cleanPhone("+7 700 123 45 67", false) // Результат: "7001234567"
   */

  public cleanPhone(phoneNumber: string | number, countryCode: boolean = true) {
    const cleanedPhone = phoneNumber.toString().replace(/\D/g, "");
    let phone = cleanedPhone;

    if (phone.length === 10 && countryCode) phone = `7${phone}`;
    if (phone.length === 11) phone = (countryCode ? "7" : "") + phone.slice(1);

    return phone;
  }

  //

  public upFirstLetter(str: string) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  //

  /**
   * @example
   * duration(86400000) // "1 день"
   * duration(90061000, { largest: 2 }) // "1 день, 1 час"
   * duration(90061000, { language: "en", largest: 2 }) // "1 day, 1 hour"
   *
   * // Пример 4: Передаём unitKits явно, чтобы включить секунды, и задаём другие настройки.
   * console.log(
   *   formatter.duration(3661000, { unitKits: "d,h,m,s", advanced: { largest: 2 } })
   * );
   * // Возможный вывод: "1 час, 1 минута"
   */

  public duration(
    ms: number,
    options?: {
      /** @default "ru" */
      language?: humanizeDuration.SupportedLanguage;
      /**
       * Максимальное число единиц для вывода.
       * @example
       * 1 // 1 день
       * 2 // 1 день, 3 часа
       * 3 // 1 день, 3 часа, 52 минуты
       * @default 1
       */
      largest?: number;
      /**
       * Нужно ли округлять последнее значение.
       * @example
       * true // 1 день, 3 часа
       * false // 1 день, 3.5 часа
       * @default true
       */
      round?: boolean;
      /** Массив юнитова для вывода, например: дни, часы, минуты и тд. */
      units?: humanizeDuration.Unit[];
      unitKits?: "d,h,m" | "d,h,m,s";
      advanced?: OmitByKey<humanizeDuration.Options, "largest" | "round" | "units">;
    },
  ) {
    const { advanced, unitKits, units: _units, ...otherOptions } = options ?? {};

    const units: humanizeDuration.Unit[] = doIt(() => {
      if (_units) return _units;
      if (!unitKits) return ["d", "h", "m"];

      if (unitKits === "d,h,m") return ["d", "h", "m"];
      if (unitKits === "d,h,m,s") return ["d", "h", "m", "s"];

      checkNever(unitKits);
      throw new Error("Unknown unitKit type");
    });

    const formatter = humanizeDuration.humanizer({
      language: "ru",
      largest: 1,
      round: true,
      units,
    });

    return formatter(ms, { ...otherOptions, ...advanced });
  }
}

//

export default new Formatter();
